import React, { MouseEvent } from 'react';
import styled from 'styled-components/macro';
import { darken } from "polished";
import { cssVarStr } from '../../utils/react';
import { Icon } from '@fluentui/react/lib/Icon';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import Flex from './Flex';

type ButtonType = 'primary' | 'normal' | 'link';
type ButtonSize = 'compact' | 'semiCompact' | 'normal';

export interface ButtonProps {
    elementType?: keyof JSX.IntrinsicElements | React.ComponentType<any>;    // button, a ...
    "data-cy"?: string;
    "aria-label"?: string;
    "aria-live"?: string;
    title?: string;
    role?: string;
    style?: React.CSSProperties;
    children?: React.ReactNode;
    buttonType?: ButtonType;
    type?: "button" | "submit" | "reset";
    borderless?: boolean;
    size?: ButtonSize;
    iconName?: string;
    disabled?: boolean;
    className?: string;
    href?: string;
    showSpinner?: boolean;
    onClick?: (e: MouseEvent) => void;
    ref?: any;
}

const ButtonComp = React.forwardRef((props: ButtonProps, ref: React.RefObject<HTMLButtonElement>) => {
    return <Button
        ref={ref}
        data-cy={props["data-cy"]}
        as={props.elementType || "button"}
        title={props.title}
        role={props.role}
        aria-label={props["aria-label"]}
        aria-live={props["aria-live"]}
        style={props.style}
        type={props.type}
        className={`
            ${props.buttonType === 'link' ? 'btn-link' : ''}
            ${props.buttonType === 'normal' ? 'btn-normal' : ''}
            ${(!props.buttonType || props.buttonType === 'primary') ? 'btn-primary' : ''}
            ${props.size === 'compact' ? 'btn-compact' : ''}
            ${props.size === 'semiCompact' ? 'btn-semi-compact' : ''}
            ${props.borderless ? 'border-less' : ''}
            ${props.className}`
        }
        onClick={e => { if (props.onClick) { props.onClick(e) } }}
        href={props.href}
        disabled={props.disabled}>
        {props.iconName && <Icon className="icon" iconName={props.iconName} />}
        {props.children}
        {props.showSpinner && <Flex><span>&nbsp;&nbsp;</span><Spinner size={SpinnerSize.small} /></Flex>}
    </Button >
});

ButtonComp.displayName = "Button";
export default ButtonComp;

const Button = styled.button`
    display: inline-flex;
    align-items: center;
    border: none;
    border-radius: .2rem;
    font-size: 1.8rem;
    font-weight: 600;
    min-height: 4rem;
    padding: 0 1.2rem;

    &.btn-primary {
        background-color: var(--color-primary);
        color: var(--color-on-primary);
        border: solid .1rem ${darken(0.3, cssVarStr("--color-primary"))};

        &:hover {
            background-color: ${darken(0.15, cssVarStr("--color-primary"))};
        }

        &:disabled, &:disabled:hover {
            background-color: var(--color-on-surface-light);
            cursor: default;
        }
    }

    &.btn-link {
        background-color: transparent;
        color: var(--color-on-surface-link);
        cursor: pointer;
        border: none;

        &:hover {
            color: ${darken(0.25, cssVarStr("--color-primary"))};
        }

        &:disabled, &:disabled:hover {
            background-color: transparent;
            color: var(--color-on-surface-light);
            cursor: default;
        }
    }

    &.btn-normal {
        background-color: var(--color-background);
        color: var(--color-on-surface);
        border: solid .2rem var(--color-surface-dark1);

        &:hover {
            background-color: ${darken(0.15, cssVarStr("--color-background"))};
        }

        &:disabled, &:disabled:hover {
            background-color: var(--color-background);
            cursor: default;
        }
    }

    &.btn-compact {
        min-height: unset;
        height: auto;
        padding: 0;
    }

    &.btn-semi-compact {
        min-height: unset;
        height: auto;
        padding: 0.2rem .5rem;
    }

    &:focus {
        outline: solid .1rem var(--color-primary);
        border: solid .1rem var(--color-on-primary);
    }

    &.border-less, &.border-less:focus {
        border: none;
    }

    .icon {
        margin-right: .4rem;
        font-size: .9em;
    }
`;
